import React, { useState, useEffect, useCallback } from 'react';
import { labelAPI, millPackAPI } from '../../services/millAPI';
import './Mill.css';

// ── payload types ───────────────────────────────────────────────────────────
interface QueueRow {
  order_id: number;
  order_number: string;
  customer: string | null;
  ship_date: string | null;
  status: string;
  ordered_lbs: number | string;
  milled_lbs: number | string;
  pct: number;
  has_pallets: boolean;
}
interface FillJSON {
  fill_id?: number;
  formula: string | null;
  formula_code: string | null;
  batch_id: number | null;
  batch_number: string | null;
  package_size: string;
  quantity_lbs: number | string;
  lot_date: string | null;
  physical_bag_number: number | null;
}
interface PalletJSON {
  pallet_id?: number;
  sequence_number: number;
  net_weight_lbs: number | string;
  target_weight_lbs: number | string;
  is_oversize: boolean;
  status?: string;
  fills: FillJSON[];
}
interface RollupLine {
  order_item_id: number;
  formula: string | null;
  ordered_lbs: number | string;
  milled_lbs: number | string;
  remaining_lbs: number | string;
}
interface Rollup {
  ordered_lbs: number | string;
  milled_lbs: number | string;
  remaining_lbs: number | string;
  pct: number;
  lines: RollupLine[];
}
interface BolStatus {
  bol_id: number;
  bol_number: string;
  status: string;
  is_stale: boolean;
  printed: boolean;
}
interface AllocationJSON {
  id: number;
  batch_number: string;
  formula_id: number | null;
  formula_name: string;
  is_pelleted: boolean;
  package_type: string;
  bags: number;
  allocated_lbs: number;
  milled_date: string | null;
}
interface Workspace {
  order_id: number;
  order_number: string;
  order_status: string;
  locked: boolean;
  milled: Rollup;
  pallets: PalletJSON[];
  source: 'persisted' | 'suggested';
  bol: BolStatus | null;
  allocations?: AllocationJSON[];
}

const n = (v: number | string | null | undefined): number => Number(v ?? 0);
const CONFIRMED_STATES = ['PACKAGING', 'READY_TO_SHIP'];

const MillPackaging: React.FC = () => {
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [ws, setWs] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busy, setBusy] = useState(false);

  const showToast = (m: string) => { setToast(m); window.setTimeout(() => setToast(''), 3500); };

  const loadQueue = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await millPackAPI.queue();
      setQueue(r.data.orders || []);
    } catch { setError('Failed to load the packaging queue'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const openOrder = useCallback(async (orderId: number) => {
    setLoading(true); setError('');
    try {
      const r = await millPackAPI.workspace(orderId);
      setWs(r.data);
    } catch { setError('Failed to open the order'); }
    finally { setLoading(false); }
  }, []);

  const reload = useCallback(async () => {
    if (ws) { const r = await millPackAPI.workspace(ws.order_id); setWs(r.data); }
  }, [ws]);

  // ── mutations ──────────────────────────────────────────────────────────────
  const persist = async (regenerate = false) => {
    if (!ws) return;
    setBusy(true); setError('');
    try {
      await millPackAPI.persist(ws.order_id, regenerate);
      await reload();
      showToast(regenerate ? 'Suggestion regenerated' : 'Build saved');
    } catch (e: any) { setError(e?.response?.data?.error || 'Save failed'); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!ws) return;
    setBusy(true); setError('');
    try {
      const r = await millPackAPI.confirm(ws.order_id);
      await reload();
      if (r.data.is_repack) {
        const action = r.data.bol?.[0]?.action;
        if (action === 'rebuilt') showToast('BOL updated');
        else if (action === 'marked_stale') showToast('BOL marked out of date');
        else showToast('Pack re-confirmed');
      } else if (r.data.fully_packed) {
        showToast('Order fully packed → Packaging');
      } else {
        showToast("Partial pack saved — won't ship until fully packed");
      }
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error || 'Confirm failed');
    }
    finally { setBusy(false); }
  };

  const regenerateBol = async () => {
    if (!ws) return;
    setBusy(true); setError('');
    try { await millPackAPI.regenerateBol(ws.order_id); await reload(); showToast('BOL regenerated'); }
    catch (e: any) { setError(e?.response?.data?.error || 'Regenerate failed'); }
    finally { setBusy(false); }
  };

  const moveFill = async (fill: FillJSON, targetSeq: number) => {
    if (!ws || fill.fill_id == null) return;
    const target = ws.pallets.find(p => p.sequence_number === targetSeq);
    if (!target || target.pallet_id == null) { setError(`No pallet #${targetSeq}`); return; }
    setBusy(true); setError('');
    try {
      await millPackAPI.moveFill(target.pallet_id, fill.fill_id);
      await reload();
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.locked ? d.error : (d?.error || 'Move failed'));
      await reload();
    }
    finally { setBusy(false); }
  };

  const splitPallet = async (p: PalletJSON) => {
    if (!ws || p.pallet_id == null) return;
    setBusy(true);
    try { await millPackAPI.splitPallet(p.pallet_id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error || 'Split failed'); }
    finally { setBusy(false); }
  };

  const mergePallet = async (p: PalletJSON, otherSeq: number) => {
    if (!ws || p.pallet_id == null) return;
    const other = ws.pallets.find(x => x.sequence_number === otherSeq);
    if (!other || other.pallet_id == null) { setError(`No pallet #${otherSeq}`); return; }
    setBusy(true);
    try { await millPackAPI.mergePallets(p.pallet_id, other.pallet_id); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error || 'Merge failed'); }
    finally { setBusy(false); }
  };

  const printSheets = () => {
    if (!ws) return;
    millPackAPI.orderPalletSheets(ws.order_id).then(r => {
      const w = window.open('', '_blank');
      if (w) { w.document.write(r.data.print_html); w.document.close(); w.print(); }
    }).catch(() => setError('Failed to load pallet sheets'));
  };

  // B6: Regenerate deletes the persisted pallets and rebuilds the
  // suggestion — silently destroying any manual moves/splits/merges.
  // Whether the persisted build contains manual edits is NOT knowable
  // (no dirty flag on Pallet rows; edits could be from a prior session),
  // so the guard errs safe and always confirms before a persisted build
  // is discarded. (The button is disabled for un-persisted suggestions,
  // where there is nothing to lose.)
  const regenerateWithGuard = () => {
    if (!window.confirm(
      'This will discard your manual pallet changes and rebuild the suggestion. Regenerate anyway?')) {
      return;
    }
    persist(true);
  };

  // Bag tags — activates the existing batch-correct label path
  // (/labels/print/): the REAL allocation.batch number + mill date are
  // overlaid on the formula's tag artwork, and every print writes a
  // LabelPrintLog row. No batch/date override is exposed here on purpose.
  // (TODO seam: a STORE_BAGGED disposition-leg-keyed variant for
  // store-bound bagging is a separate follow-on build.)
  const [printingTagsFor, setPrintingTagsFor] = useState<number | null>(null);
  const printBagTags = async (alloc: AllocationJSON) => {
    setPrintingTagsFor(alloc.id);
    setError('');
    try {
      const form = alloc.is_pelleted ? 'PELLET' : 'MASH';
      const match = await labelAPI.findForFormula({
        formula_id: alloc.formula_id ?? undefined,
        form,
        formula_name: alloc.formula_name,
      });
      const template = match.data.template || match.data;
      if (!template?.id) {
        setError(`No ${form.toLowerCase()} label template found for ${alloc.formula_name} — upload one in Label Management.`);
        return;
      }
      const res = await labelAPI.printLabels({
        allocation_id: alloc.id,
        template_id: template.id,
        quantity: alloc.bags || 1,
      });
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(
          `<html><head><title>Bag tags — ${alloc.batch_number}</title></head>` +
          `<body style="margin:0"><img src="${res.data.image}" style="width:100%" ` +
          `onload="window.print()" /></body></html>`);
        w.document.close();
      }
      showToast(`Tags generated for ${alloc.batch_number} (${res.data.quantity} label(s), logged)`);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Bag tag print failed');
    } finally {
      setPrintingTagsFor(null);
    }
  };

  // ── render: queue ───────────────────────────────────────────────────────────
  if (!ws) {
    return (
      <div className="mill-page">
        <h1>Packaging Station</h1>
        {error && <div className="mill-error">{error}</div>}
        {loading && <div className="mill-loading">Loading…</div>}
        <div className="pack-queue">
          <div className="pack-queue-head">Ready to pack — by ship date</div>
          {queue.length === 0 && !loading && <div className="mill-empty">Nothing waiting to pack.</div>}
          {queue.map(q => (
            <button key={q.order_id} className="pack-queue-row" onClick={() => openOrder(q.order_id)}>
              <div className="pq-main">
                <span className="pq-order">#{q.order_number}</span>
                <span className="pq-cust">{q.customer}</span>
              </div>
              <div className="pq-meta">
                <span className="pq-ship">Ship {q.ship_date || '—'}</span>
                <span className="pq-milled">{Math.round(n(q.milled_lbs)).toLocaleString()} / {Math.round(n(q.ordered_lbs)).toLocaleString()} lb milled</span>
                <span className={`pq-badge ${q.has_pallets ? 'built' : ''}`}>{q.has_pallets ? 'Pallets built' : 'Not built'}</span>
              </div>
              <div className="pq-bar"><div className="pq-bar-fill" style={{ width: `${Math.min(100, q.pct)}%` }} /></div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── render: workspace ───────────────────────────────────────────────────────
  const confirmed = CONFIRMED_STATES.includes(ws.order_status);
  const locked = ws.locked;
  const rollup = ws.milled;
  const totalPallets = ws.pallets.length;
  const seqs = ws.pallets.map(p => p.sequence_number);

  return (
    <div className="mill-page">
      {toast && <div className="mill-toast">{toast}</div>}

      <div className="pack-ws-top">
        <button className="mill-back" onClick={() => { setWs(null); loadQueue(); }}>← Queue</button>
        <h1>Pack #{ws.order_number}</h1>
      </div>

      {locked && <div className="pack-banner locked">Order shipped — pallets locked.</div>}
      {ws.bol?.is_stale && !locked && (
        <div className="pack-banner stale">
          BOL {ws.bol.bol_number} is out of date.
          <button className="mill-btn small" disabled={busy} onClick={regenerateBol}>Regenerate BOL</button>
        </div>
      )}
      {error && <div className="mill-error">{error}</div>}

      {/* Milled X of Y */}
      <div className="pack-progress">
        <div className="pack-progress-head">
          Milled {Math.round(n(rollup.milled_lbs)).toLocaleString()} of {Math.round(n(rollup.ordered_lbs)).toLocaleString()} lb ({rollup.pct}%)
        </div>
        <div className="pq-bar big"><div className="pq-bar-fill" style={{ width: `${Math.min(100, rollup.pct)}%` }} /></div>
        <details className="pack-lines">
          <summary>Per-line detail</summary>
          {rollup.lines.map(l => (
            <div key={l.order_item_id} className="pack-line">
              <span>{l.formula}</span>
              <span>{Math.round(n(l.milled_lbs)).toLocaleString()} / {Math.round(n(l.ordered_lbs)).toLocaleString()} lb</span>
            </div>
          ))}
        </details>
      </div>

      {/* Actions */}
      <div className="pack-actions">
        {ws.source === 'suggested'
          ? <button className="mill-btn primary" disabled={busy || locked} onClick={() => persist(false)}>Save build</button>
          : <button className="mill-btn primary" disabled={busy || locked} onClick={confirm}>{confirmed ? 'Re-confirm pack' : 'Confirm pack'}</button>}
        <button className="mill-btn" disabled={busy || locked || ws.source === 'suggested'} onClick={regenerateWithGuard}>Regenerate suggestion</button>
        <button className="mill-btn" disabled={ws.source === 'suggested'} onClick={printSheets}>Print pallet sheets</button>
      </div>
      {ws.source === 'suggested' && <div className="pack-hint">This is a suggested build — Save build to persist, then assign pallet #s.</div>}

      {/* Bag tags — per batch allocation (wholesale bagged lines) */}
      {(ws.allocations || []).filter(a => a.package_type === 'BAGS' || a.package_type === 'BAGS_19').length > 0 && (
        <div className="mill-section" style={{ margin: '0.75rem 0', padding: '0.75rem' }}>
          <strong>Bag tags</strong>
          <div className="pack-hint">
            Prints the real batch # + mill date on the formula&apos;s tag artwork; every print is logged.
          </div>
          {(ws.allocations || [])
            .filter(a => a.package_type === 'BAGS' || a.package_type === 'BAGS_19')
            .map(a => (
              <div key={a.id} className="pack-line" style={{ alignItems: 'center' }}>
                <span>
                  {a.formula_name} ({a.is_pelleted ? 'Pellet' : 'Mash'}) —{' '}
                  <strong>{a.batch_number}</strong>
                  {a.milled_date && ` · milled ${a.milled_date}`} · {a.bags} bag(s)
                </span>
                <button
                  className="mill-btn small"
                  disabled={printingTagsFor === a.id}
                  onClick={() => printBagTags(a)}
                >
                  {printingTagsFor === a.id ? 'Generating…' : `Print ${a.bags || 1} tag(s)`}
                </button>
              </div>
            ))}
        </div>
      )}

      {/* Pallet assignment */}
      <div className="pack-grid">
        <div className="pack-col">
          <h3>Feed items</h3>
          {ws.pallets.flatMap(p => p.fills.map((f, i) => (
            <div key={`${p.sequence_number}-${f.fill_id ?? i}`} className="pack-item">
              <div className="pi-main">
                <span className="pi-formula">{f.formula}</span>
                <span className="pi-batch">{f.batch_number}</span>
              </div>
              <div className="pi-meta">
                <span>{f.package_size === 'super_sack' ? 'Super Sack' : `${f.package_size}# bag`}</span>
                <span>{Math.round(n(f.quantity_lbs)).toLocaleString()} lb</span>
              </div>
              <label className="pi-pallet">
                Pallet #
                <input
                  type="number" min={1} defaultValue={p.sequence_number}
                  disabled={locked || busy || f.fill_id == null}
                  onBlur={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v) && v !== p.sequence_number) moveFill(f, v);
                  }}
                />
              </label>
            </div>
          )))}
        </div>

        <div className="pack-col">
          <h3>Pallets ({totalPallets})</h3>
          {ws.pallets.map(p => {
            const net = n(p.net_weight_lbs), cap = n(p.target_weight_lbs);
            const over = !p.is_oversize && net > cap;
            return (
              <div key={p.sequence_number} className={`pack-pallet ${over ? 'over' : ''}`}>
                <div className="pp-head">
                  <b>Pallet {p.sequence_number}</b>
                  <span className={over ? 'pp-w over' : 'pp-w'}>{Math.round(net).toLocaleString()} / {Math.round(cap).toLocaleString()} lb{p.is_oversize ? ' (oversize)' : ''}</span>
                </div>
                <div className="pq-bar"><div className={`pq-bar-fill ${over ? 'over' : ''}`} style={{ width: `${Math.min(100, cap ? (net / cap) * 100 : 0)}%` }} /></div>
                {over && <div className="pp-err">Over 2,600 lb cap — reassign items.</div>}
                {!locked && p.pallet_id != null && (
                  <div className="pp-tools">
                    {p.fills.length >= 2 && <button className="mill-btn tiny" disabled={busy} onClick={() => splitPallet(p)}>Split</button>}
                    {seqs.filter(s => s !== p.sequence_number).map(s => (
                      <button key={s} className="mill-btn tiny" disabled={busy} onClick={() => mergePallet(p, s)}>Merge #{s}→</button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MillPackaging;
